import React, { useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useParams } from "react-router";
import { useQuery } from "@apollo/client/react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AddProjectSharingModal from "./AddProjectSharingModal";
import ProjectSharingSkeleton from "./ProjectSharingSkeleton";
import ConfirmDeleteModal from "../../../components/images/ConfirmDeleteModal";
import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import { SHARED_CLIENT_PROJECTS_QUERY } from "../../../constants/graphql/queries";
import { REMOVE_SHARED_CLIENT_PROJECT_MUTATION } from "../../../constants/graphql/mutations";

export default function ProjectSharing({ children, settings }) {
  const [open, setOpen] = useState(false);
  const [selectedSharedProject, setSelectedSharedProject] = useState(null);
  const { projectId } = useParams();
  const label = settings?.filter_field1;
  const { data, loading, refetch } = useQuery(SHARED_CLIENT_PROJECTS_QUERY, {
    variables: { projectId },
    fetchPolicy: "network-only",
  });
  const [removeSharedClientProject] = useMutation(REMOVE_SHARED_CLIENT_PROJECT_MUTATION);
  const { notifySuccess, notifyError } = useNotification();
  const sharedClientProjects = data?.sharedClientProjects || [];

  const handleToggle = () => {
    setOpen(v => !v);
    refetch();
  };

  const handleOpenDeleteModal = sharedProject => {
    setSelectedSharedProject(sharedProject);
  };

  const handleCloseDeleteModal = () => {
    setSelectedSharedProject(null);
  };

  const handleDelete = () => {
    if (!selectedSharedProject) return;

    return removeSharedClientProject({
      variables: { projectId: selectedSharedProject.id, clientId: selectedSharedProject.client.id },
      onSuccess: () => {
        refetch();
        notifySuccess(`${label || "Project"} sharing has been successfully removed`);
      },
      onFailure: errors => {
        notifyError(errors);
        console.error(`Failed to remove ${label || "Project"} sharing:`, errors);
      },
    });
  };

  return (
    <>
      <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
        {children}
        <Button
          variant="contained"
          onClick={handleToggle}
          startIcon={<AddCircleOutlineIcon fontSize="small" />}
        >
          {`Add ${label || "Project"} Sharing`}
        </Button>
      </Box>
      <Box p={2}>
        <TableContainer component={Paper}>
          <Table aria-label="users table">
            <TableHead>
              <TableRow>
                <TableCell>Client Name</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <ProjectSharingSkeleton />
              ) : sharedClientProjects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    {`No ${label?.toLowerCase() || "project"} sharing found`}
                  </TableCell>
                </TableRow>
              ) : (
                sharedClientProjects.map(sharedProject => (
                  <TableRow
                    key={sharedProject.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    hover
                  >
                    <TableCell component="th" scope="row">
                      {sharedProject.client?.name}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip arrow title="Delete">
                        <IconButton
                          onClick={() => handleOpenDeleteModal(sharedProject)}
                          size="small"
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {open && <AddProjectSharingModal onClose={handleToggle} />}
      {selectedSharedProject && (
        <ConfirmDeleteModal
          title="Delete Sharing"
          descr={`Are you sure you want to remove ${selectedSharedProject?.client?.name ||
            ""} from ${label || "Project"} sharing?`}
          onClose={handleCloseDeleteModal}
          onDelete={handleDelete}
        />
      )}
    </>
  );
}
